import { RowButton, TextButton } from "@/components/ui/pressable";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { useGetList, useGetOne } from "ra-core";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Flag,
  FolderKanban,
} from "lucide-react";
import type {
  Company,
  OrganizationMember,
  Task,
} from "@/components/atomic-crm/types";
import {
  addDays,
  isSameDay,
  parseDateKey,
  toDateKey,
  type CalendarEvent,
} from "@/modules/calendar/calendarUtils";
import { canRescheduleCalendarEvent } from "@/modules/calendar/calendarEventReschedule";
import { getWorkCategoryMeta } from "@/modules/work/workCategoryUtils";
import {
  buildTimelineGroups,
  clampTimelineDayWidth,
  getTimelineDayIndex,
  getTimelineMonthSegments,
  TIMELINE_CARD_MIN_PX,
  type TimelineGroup,
  type TimelinePlacedItem,
  type TimelineProjectSpan,
  type TimelineSummary,
  type TimelineRange,
} from "@/modules/work/workTimeline";
import type { WorkItem } from "@/modules/work/workTypes";
import { WorkItemSummary } from "@/modules/work/WorkItemSummary";
import { TaskAssignedAvatars } from "@/components/atomic-crm/tasks/TaskAssignedAvatars";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initialsOf } from "@/components/avatar/resolveAvatar";
import { SignedMemberAvatarImage } from "@/components/avatar/SignedMemberAvatarImage";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const LABEL_WIDTH_DEFAULT = 224;
const LABEL_WIDTH_MIN = 160;
const LABEL_WIDTH_MAX = 520;
const LABEL_WIDTH_KEY = "nomi.work.timeline.labelWidth";
/** Rows read the label column width from this CSS variable. */
const LABEL_WIDTH_VAR = "var(--timeline-label-width)";

const clampLabelWidth = (value: number) =>
  Math.min(LABEL_WIDTH_MAX, Math.max(LABEL_WIDTH_MIN, Math.round(value)));

/** The label column width, remembered in this browser. */
const useTimelineLabelWidth = () => {
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(LABEL_WIDTH_KEY));
      return saved > 0 ? clampLabelWidth(saved) : LABEL_WIDTH_DEFAULT;
    } catch {
      return LABEL_WIDTH_DEFAULT;
    }
  });
  const save = useCallback((next: number) => {
    const value = clampLabelWidth(next);
    setWidth(value);
    try {
      window.localStorage.setItem(LABEL_WIDTH_KEY, String(value));
    } catch {
      // Private mode: the width lasts until reload.
    }
  }, []);
  return [width, save] as const;
};

/** Drag the right edge of the label column to resize it (double-click resets). */
const LabelColumnResizer = ({
  width,
  onResize,
}: {
  width: number;
  onResize: (next: number) => void;
}) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label="Resize the name column"
    aria-valuenow={width}
    aria-valuemin={LABEL_WIDTH_MIN}
    aria-valuemax={LABEL_WIDTH_MAX}
    tabIndex={0}
    title="Drag to resize · double-click to reset"
    className="absolute inset-y-0 -right-1 z-50 w-2 cursor-col-resize after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent hover:after:bg-primary focus-visible:after:bg-primary"
    onPointerDown={(event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      const move = (moveEvent: PointerEvent) =>
        onResize(startWidth + moveEvent.clientX - startX);
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.style.removeProperty("cursor");
      };
      document.body.style.cursor = "col-resize";
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    }}
    onDoubleClick={() => onResize(LABEL_WIDTH_DEFAULT)}
    onKeyDown={(event) => {
      if (event.key === "ArrowLeft") onResize(width - 16);
      if (event.key === "ArrowRight") onResize(width + 16);
    }}
  />
);
const LANE_HEIGHT = 40;
const ROW_PADDING = 8;
const COLLAPSED_HEIGHT = 44;
const SECTION_HEIGHT = 36;
/** Project summary bar strip at the top of a project row. */
const SUMMARY_HEIGHT = 30;
/** Card: date strip + eyebrow, title, description and avatars. */
const CARD_HEIGHT = 96;
const CARD_LANE_HEIGHT = CARD_HEIGHT + 8;
const PROJECTS_SECTION_KEY = "section:projects";

const isWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6;

type TimelineDragMode = "move" | "start" | "end";

type TimelineDrag = {
  item: WorkItem;
  mode: TimelineDragMode;
  /** Days between the item's first day and the grabbed column (move only). */
  grabOffset: number;
};

const isTaskRange = (item: WorkItem) =>
  item.event.kind === "task" &&
  Boolean(item.startDate && item.startDate < item.date);

const ResizeHandle = ({
  side,
  onDragStart,
}: {
  side: "start" | "end";
  onDragStart: (event: DragEvent<HTMLSpanElement>) => void;
}) => (
  <span
    draggable
    onDragStart={onDragStart}
    onClick={(event) => event.stopPropagation()}
    aria-hidden
    className={cn(
      "absolute inset-y-0 z-10 w-2 cursor-ew-resize opacity-0 transition-opacity group-hover/timeline-item:opacity-100",
      side === "start" ? "left-0" : "right-0",
    )}
  >
    <span
      className={cn(
        "absolute top-1/2 h-3.5 w-1 -translate-y-1/2 rounded-full bg-current opacity-50",
        side === "start" ? "left-0.5" : "right-0.5",
      )}
    />
  </span>
);

/** Whole days an open item is past due (0 when not overdue). */
const overdueDays = (item: WorkItem, todayKey: string) =>
  item.overdue && !item.done
    ? Math.max(
        1,
        Math.round(
          (parseDateKey(todayKey).getTime() -
            parseDateKey(item.date).getTime()) /
            (24 * 60 * 60 * 1000),
        ),
      )
    : 0;

const TimelineItem = ({
  placed,
  rangeStart,
  cardTopOffset,
  topOffset,
  dayWidth,
  compact,
  todayKey,
  summaryOpen,
  onSummaryOpenChange,
  onSelect,
  onDragStart,
}: {
  placed: TimelinePlacedItem;
  rangeStart: Date;
  /** Top of the card lanes. */
  cardTopOffset: number;
  /** Top of the regular lanes (below the card lanes). */
  topOffset: number;
  dayWidth: number;
  /** Compact row: marker / bar only, no outside title. */
  compact: boolean;
  todayKey: string;
  summaryOpen: boolean;
  onSummaryOpenChange: (open: boolean) => void;
  onSelect: (item: WorkItem) => void;
  onDragStart: (drag: TimelineDrag) => void;
}) => {
  const {
    item,
    dayIndex,
    barDays,
    labelInside,
    clippedStart,
    clippedEnd,
    span,
  } = placed;
  const lane = compact ? placed.compactLane : placed.lane;
  const top = placed.card
    ? cardTopOffset + lane * CARD_LANE_HEIGHT
    : topOffset + lane * LANE_HEIGHT;
  const category = getWorkCategoryMeta(item.category);
  const draggable = canRescheduleCalendarEvent(item.event);
  const isRange = isTaskRange(item);
  const task = item.event.kind === "task" ? item.event.task : null;
  const firstDayIndex = getTimelineDayIndex(
    rangeStart,
    isRange ? item.startDate! : item.date,
  );
  const lateDays = overdueDays(item, todayKey);
  // Past meetings / events fade a little; completed items fade and strike.
  const isPastEvent = !task && !item.done && item.date < todayKey;
  const isMilestone = barDays === 1 && !isRange && !labelInside;
  const showOutsideLabel = !labelInside && !compact;

  const avatars = task ? (
    <TaskAssignedAvatars task={task} size="xs" linked={false} hideEmpty />
  ) : null;

  const subtitle = lateDays
    ? `${lateDays}d overdue${item.time ? ` · ${item.time}` : ""}`
    : item.time;

  const label = (
    <span
      className={cn(
        "flex min-w-0 items-center gap-1.5",
        labelInside
          ? "flex-1"
          : // Outside label: own background so grid lines and the today line never cross it.
            "max-w-full rounded-md bg-card/95 py-0.5 pr-1.5 pl-1 shadow-sm ring-1",
        !labelInside && (lateDays ? "ring-destructive/40" : "ring-border/50"),
      )}
    >
      {labelInside ? null : avatars}
      <span className="flex min-w-0 flex-col leading-tight">
        <span
          className={cn(
            "truncate text-xs font-medium",
            labelInside
              ? "text-current"
              : lateDays
                ? "text-destructive group-hover/timeline-item:underline"
                : "text-foreground group-hover/timeline-item:underline",
            item.done && "text-muted-foreground line-through",
          )}
        >
          {item.title}
        </span>
        {subtitle && !labelInside ? (
          <span
            className={cn(
              "truncate text-2xs",
              lateDays
                ? "font-medium text-destructive"
                : "text-muted-foreground",
            )}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
    </span>
  );

  const status = item.done ? (
    <Check className="size-3.5 shrink-0" />
  ) : (
    <span className={cn("size-2 shrink-0 rounded-full", category.dotClass)} />
  );

  return (
    <Popover open={summaryOpen} onOpenChange={onSummaryOpenChange}>
      <PopoverTrigger asChild>
        <RowButton
          layout="none"
          density="none"
          className={cn(
            "group/timeline-item absolute flex items-center gap-1 text-left transition-opacity",
            !placed.card && "h-8",
            draggable && "cursor-grab active:cursor-grabbing",
            item.done && "opacity-45 hover:opacity-80",
            isPastEvent && "opacity-60 hover:opacity-90",
          )}
          draggable={draggable}
          onDragStart={(event) => {
            onSummaryOpenChange(false);
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", item.id);
            const rect = event.currentTarget.getBoundingClientRect();
            const pointerDay =
              dayIndex + Math.floor((event.clientX - rect.left) / dayWidth);
            onDragStart({
              item,
              mode: "move",
              grabOffset: isRange ? pointerDay - firstDayIndex : 0,
            });
          }}
          title={`${item.title}${item.time ? ` · ${item.time}` : ""}${
            lateDays ? ` · ${lateDays}d overdue` : ""
          }${item.done ? " · Done" : ""}`}
          style={{
            left: dayIndex * dayWidth,
            width: (showOutsideLabel ? span : barDays) * dayWidth,
            top,
            height: placed.card ? CARD_HEIGHT : undefined,
          }}
        >
          {placed.card ? (
            <TimelineSpanCard
              startDate={item.startDate ?? null}
              endDate={item.date}
              eyebrow={item.dealName ?? category.label}
              title={item.title}
              description={subtitle}
              descriptionTone={lateDays ? "destructive" : "muted"}
              avatars={avatars}
              headerClass={item.done ? "bg-muted" : category.chipClass}
              clippedStart={clippedStart}
              clippedEnd={clippedEnd}
              done={item.done}
              overdue={lateDays > 0}
              width={barDays * dayWidth}
            >
              {draggable ? (
                <>
                  {clippedStart ? null : (
                    <ResizeHandle
                      side="start"
                      onDragStart={(event) => {
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);
                        onDragStart({ item, mode: "start", grabOffset: 0 });
                      }}
                    />
                  )}
                  {clippedEnd ? null : (
                    <ResizeHandle
                      side="end"
                      onDragStart={(event) => {
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);
                        onDragStart({ item, mode: "end", grabOffset: 0 });
                      }}
                    />
                  )}
                </>
              ) : null}
            </TimelineSpanCard>
          ) : isMilestone ? (
            /* Single-day item: a milestone diamond centred on its day. */
            <span
              className="flex h-7 shrink-0 items-center justify-center"
              style={{ width: dayWidth }}
            >
              <span
                className={cn(
                  "flex rotate-45 items-center justify-center rounded-[3px] border-2 border-card shadow-sm transition-transform group-hover/timeline-item:scale-110",
                  item.done ? "bg-muted-foreground/60" : category.dotClass,
                  lateDays > 0 && "ring-2 ring-destructive",
                )}
                style={{
                  width: Math.min(16, Math.max(10, dayWidth - 14)),
                  height: Math.min(16, Math.max(10, dayWidth - 14)),
                }}
              >
                {item.done ? (
                  <Check className="size-2.5 -rotate-45 text-card" />
                ) : null}
              </span>
            </span>
          ) : (
            /* The bar covers exactly the scheduled days. */
            <span
              className={cn(
                "relative flex h-7 shrink-0 items-center justify-start gap-1.5 overflow-hidden border px-2 shadow-sm transition-shadow group-hover/timeline-item:shadow-overlay",
                clippedStart
                  ? "rounded-l-none border-l-0"
                  : "ml-[3px] rounded-l-md",
                clippedEnd ? "rounded-r-none border-r-0" : "rounded-r-md",
                item.done
                  ? "border-border bg-muted text-muted-foreground"
                  : category.chipClass,
                lateDays > 0 && "border-destructive ring-1 ring-destructive",
              )}
              style={{
                width:
                  barDays * dayWidth -
                  (clippedStart ? 0 : 3) -
                  (clippedEnd ? 0 : 3),
              }}
            >
              {status}
              {labelInside && barDays * dayWidth >= 150 ? avatars : null}
              {labelInside ? label : null}
              {isRange && draggable ? (
                <>
                  {clippedStart ? null : (
                    <ResizeHandle
                      side="start"
                      onDragStart={(event) => {
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);
                        onDragStart({ item, mode: "start", grabOffset: 0 });
                      }}
                    />
                  )}
                  {clippedEnd ? null : (
                    <ResizeHandle
                      side="end"
                      onDragStart={(event) => {
                        event.stopPropagation();
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);
                        onDragStart({ item, mode: "end", grabOffset: 0 });
                      }}
                    />
                  )}
                </>
              ) : null}
            </span>
          )}
          {showOutsideLabel && !placed.card ? label : null}
        </RowButton>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        collisionPadding={16}
        className="w-auto p-0"
      >
        <WorkItemSummary
          item={item}
          onEdit={(selected) => {
            onSummaryOpenChange(false);
            onSelect(selected);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const formatShortDate = (dateKey: string | null) =>
  dateKey
    ? parseDateKey(dateKey).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

const formatStripDate = (dateKey: string | null) =>
  dateKey
    ? parseDateKey(dateKey).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : null;

/**
 * Card for long spans (projects, multi-day tasks): a tinted strip with the
 * start and end dates, then eyebrow, title, description and avatars.
 */
const TimelineSpanCard = ({
  startDate,
  endDate,
  eyebrow,
  title,
  description,
  descriptionTone = "muted",
  avatars,
  headerClass,
  clippedStart,
  clippedEnd,
  done = false,
  overdue = false,
  width,
  children,
}: {
  startDate: string | null;
  endDate: string | null;
  eyebrow?: string | null;
  title: string;
  description?: string | null;
  descriptionTone?: "muted" | "destructive";
  avatars?: ReactNode;
  headerClass: string;
  clippedStart: boolean;
  clippedEnd: boolean;
  done?: boolean;
  overdue?: boolean;
  width: number;
  children?: ReactNode;
}) => {
  const start = formatStripDate(startDate);
  const end = formatStripDate(endDate);
  return (
    <span
      className={cn(
        "relative flex h-full shrink-0 flex-col overflow-hidden border bg-card text-left shadow-sm transition-shadow group-hover/timeline-item:shadow-overlay",
        clippedStart ? "rounded-l-none border-l-0" : "ml-[3px] rounded-l-lg",
        clippedEnd ? "rounded-r-none border-r-0" : "rounded-r-lg",
        overdue && "border-destructive ring-1 ring-destructive",
      )}
      style={{
        width: width - (clippedStart ? 0 : 3) - (clippedEnd ? 0 : 3),
      }}
    >
      <span
        className={cn(
          "flex h-5 shrink-0 items-center justify-between gap-2 border-0 border-b px-2.5 text-2xs tabular-nums",
          headerClass,
        )}
      >
        <span className="truncate">{clippedStart ? `← ${start}` : start}</span>
        {end && end !== start ? (
          <span className="shrink-0">{clippedEnd ? `${end} →` : end}</span>
        ) : null}
      </span>
      <span className="flex min-h-0 flex-1 flex-col gap-0.5 px-2.5 pt-1.5 pb-2">
        {eyebrow ? (
          <span className="truncate text-2xs leading-tight text-muted-foreground">
            {eyebrow}
          </span>
        ) : null}
        <span
          className={cn(
            "truncate text-sm leading-tight font-semibold text-foreground group-hover/timeline-item:underline",
            done && "text-muted-foreground line-through",
          )}
        >
          {title}
        </span>
        {description ? (
          <span
            className={cn(
              "truncate text-2xs leading-tight",
              descriptionTone === "destructive"
                ? "font-medium text-destructive"
                : "text-muted-foreground",
            )}
          >
            {description}
          </span>
        ) : null}
        {avatars ? <span className="mt-auto flex">{avatars}</span> : null}
      </span>
      {children}
    </span>
  );
};

/** Stacked avatars for a project's team (no links: the card is a link). */
const ProjectMemberAvatars = ({ memberIds }: { memberIds: string[] }) => {
  const { data: members = [] } = useGetList<OrganizationMember>(
    "organization_members",
    {
      filter: { "id@in": `(${memberIds.join(",")})` },
      pagination: { page: 1, perPage: memberIds.length },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: memberIds.length > 0, staleTime: 60_000 },
  );
  if (members.length === 0) return null;
  return (
    <span className="flex -space-x-1.5">
      {members.slice(0, 5).map((member) => (
        <Avatar
          key={String(member.id)}
          className="size-5 ring-2 ring-card"
          title={[member.first_name, member.last_name]
            .filter(Boolean)
            .join(" ")}
        >
          <SignedMemberAvatarImage member={member} size={40} alt="" />
          <AvatarFallback className="bg-muted text-3xs font-medium">
            {initialsOf(member)}
          </AvatarFallback>
        </Avatar>
      ))}
    </span>
  );
};

/** Wide project span: the project's card (client, name, description, team). */
const ProjectSummaryCard = ({
  summary,
  project,
  dayWidth,
}: {
  summary: TimelineSummary;
  project: TimelineProjectSpan;
  dayWidth: number;
}) => {
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: project.companyId! },
    {
      enabled: !project.companyName && project.companyId != null,
      staleTime: 5 * 60_000,
    },
  );
  const memberIds = (project.memberIds ?? []).map(String).filter(Boolean);
  return (
    <Link
      to={`/deals/${project.dealId}/show`}
      title={project.name}
      className="group/timeline-item absolute flex"
      style={{
        left: summary.dayIndex * dayWidth,
        top: ROW_PADDING,
        height: CARD_HEIGHT,
      }}
    >
      <TimelineSpanCard
        startDate={summary.startDate}
        endDate={summary.endDate}
        eyebrow={project.companyName || company?.name}
        title={project.name}
        description={project.description?.trim() || null}
        avatars={
          memberIds.length > 0 ? (
            <ProjectMemberAvatars memberIds={memberIds} />
          ) : null
        }
        headerClass={getWorkCategoryMeta("delivery").chipClass}
        clippedStart={summary.clippedStart}
        clippedEnd={summary.clippedEnd}
        width={summary.barDays * dayWidth}
      />
    </Link>
  );
};

/** Project start → delivery bar: the "header" of a project row. */
const ProjectSummaryBar = ({
  summary,
  dealId,
  label,
  dayWidth,
}: {
  summary: TimelineSummary;
  dealId: WorkItem["dealId"];
  label: string;
  dayWidth: number;
}) => {
  const start = formatShortDate(summary.startDate);
  const end = formatShortDate(summary.endDate);
  const dates =
    start && end && start !== end
      ? `${start} → ${end}`
      : end
        ? `Delivery ${end}`
        : `Start ${start}`;
  const delivery = getWorkCategoryMeta("delivery");
  const width = summary.barDays * dayWidth;

  return (
    <Link
      to={`/deals/${dealId}/show`}
      title={`${label} · ${dates}`}
      className={cn(
        "absolute flex h-6 items-center gap-1.5 overflow-hidden border px-2 text-2xs font-semibold transition-shadow hover:shadow-overlay",
        delivery.chipClass,
        summary.clippedStart ? "rounded-l-none border-l-0" : "rounded-l-full",
        summary.clippedEnd ? "rounded-r-none border-r-0" : "rounded-r-full",
      )}
      style={{
        left: summary.dayIndex * dayWidth + (summary.clippedStart ? 0 : 2),
        width: Math.max(
          width - (summary.clippedStart ? 0 : 2) - (summary.clippedEnd ? 0 : 2),
          dayWidth - 4,
        ),
        top: ROW_PADDING,
      }}
    >
      <FolderKanban className="size-3.5 shrink-0" />
      {width >= 110 ? <span className="truncate">{dates}</span> : null}
      {summary.endDate && !summary.clippedEnd && width >= 60 ? (
        <Flag className="ml-auto size-3 shrink-0 fill-current" />
      ) : null}
    </Link>
  );
};

const TimelineSectionRow = ({
  label,
  count,
  canvasWidth,
  collapsed,
  onToggle,
}: {
  label: string;
  count: number;
  canvasWidth: number;
  collapsed: boolean;
  onToggle: () => void;
}) => (
  <div className="flex border-b" style={{ height: SECTION_HEIGHT }}>
    <RowButton
      layout="none"
      density="none"
      className="sticky left-0 z-20 flex shrink-0 items-center gap-1.5 border-r bg-muted px-3 text-left text-sm font-semibold text-foreground transition-colors hover:bg-accent"
      onClick={onToggle}
      aria-expanded={!collapsed}
      style={{ width: LABEL_WIDTH_VAR }}
    >
      {collapsed ? (
        <ChevronRight className="size-4 text-muted-foreground" />
      ) : (
        <ChevronDown className="size-4 text-muted-foreground" />
      )}
      <FolderKanban className="size-4 text-muted-foreground" />
      <span className="truncate">{label}</span>
      <span className="ml-auto shrink-0 rounded-sm bg-background px-1.5 text-2xs font-medium text-muted-foreground tabular-nums">
        {count}
      </span>
    </RowButton>
    <div className="shrink-0 bg-muted/40" style={{ width: canvasWidth }} />
  </div>
);

const TimelineGroupRow = ({
  group,
  range,
  dayWidth,
  indent = false,
  expanded,
  onToggle,
  todayKey,
  onSelectItem,
  openItemId,
  onOpenItemChange,
  onDragStart,
  onDropOnDay,
}: {
  group: TimelineGroup;
  range: TimelineRange;
  dayWidth: number;
  /** Project rows sit inside the Projects section. */
  indent?: boolean;
  /** Show every title (stacked lanes) instead of the one-line compact row. */
  expanded: boolean;
  onToggle: () => void;
  todayKey: string;
  onSelectItem: (item: WorkItem) => void;
  openItemId: string | null;
  onOpenItemChange: (itemId: string | null) => void;
  onDragStart: (drag: TimelineDrag) => void;
  onDropOnDay: (dayIndex: number) => void;
}) => {
  const summaryCard =
    group.summary != null &&
    group.project != null &&
    group.summary.barDays * dayWidth >= TIMELINE_CARD_MIN_PX;
  const summaryHeight = group.summary
    ? summaryCard
      ? CARD_LANE_HEIGHT
      : SUMMARY_HEIGHT
    : 0;
  const cardHeight = group.cardLaneCount * CARD_LANE_HEIGHT;
  // Titles only cost space when they force extra lanes; then the row starts
  // compact (markers on one line) and expands on click.
  const canExpand = group.laneCount > group.compactLaneCount;
  const compact = canExpand && !expanded;
  const usedLanes = compact ? group.compactLaneCount : group.laneCount;
  const lanes =
    group.summary || group.cardLaneCount > 0
      ? usedLanes
      : Math.max(1, usedLanes);
  const height = Math.max(
    COLLAPSED_HEIGHT,
    ROW_PADDING * 2 + summaryHeight + cardHeight + lanes * LANE_HEIGHT,
  );

  const dayIndexFromEvent = (event: DragEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return Math.min(
      range.days.length - 1,
      Math.max(0, Math.floor((event.clientX - rect.left) / dayWidth)),
    );
  };

  return (
    <div className="flex border-b last:border-b-0" style={{ height }}>
      <div
        className={cn(
          "sticky left-0 z-20 flex shrink-0 items-start gap-1.5 border-r bg-card pt-3 pr-3",
          indent ? "pl-8" : "pl-3",
        )}
        style={{ width: LABEL_WIDTH_VAR }}
      >
        {canExpand ? (
          <TextButton
            tone="muted"
            size="inherit"
            onClick={onToggle}
            className="mt-px"
            title={expanded ? "Compact row" : "Show all titles"}
            aria-label={
              expanded
                ? `Compact ${group.label}`
                : `Show all titles in ${group.label}`
            }
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </TextButton>
        ) : (
          <span className="size-4 shrink-0" aria-hidden />
        )}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {group.kind === "project" && group.dealId != null ? (
            <Link
              to={`/deals/${group.dealId}/show`}
              className="truncate text-sm font-medium text-foreground hover:underline"
            >
              {group.label}
            </Link>
          ) : (
            <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
              {group.category ? (
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    getWorkCategoryMeta(group.category).dotClass,
                  )}
                />
              ) : null}
              <span className="truncate">{group.label}</span>
            </span>
          )}
          <span className="shrink-0 rounded-sm bg-muted px-1.5 text-2xs font-medium text-muted-foreground tabular-nums">
            {group.items.length}
          </span>
        </div>
      </div>

      <div
        className="relative shrink-0"
        style={{ width: range.days.length * dayWidth }}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
        }}
        onDrop={(event) => {
          event.preventDefault();
          onDropOnDay(dayIndexFromEvent(event));
        }}
      >
        {summaryCard ? (
          <ProjectSummaryCard
            summary={group.summary!}
            project={group.project!}
            dayWidth={dayWidth}
          />
        ) : group.summary && group.dealId != null ? (
          <ProjectSummaryBar
            summary={group.summary}
            dealId={group.dealId}
            label={group.label}
            dayWidth={dayWidth}
          />
        ) : null}
        {group.items.map((placed) => (
          <TimelineItem
            key={placed.item.id}
            placed={placed}
            rangeStart={range.start}
            cardTopOffset={ROW_PADDING + summaryHeight}
            topOffset={ROW_PADDING + summaryHeight + cardHeight}
            compact={compact}
            todayKey={todayKey}
            summaryOpen={openItemId === placed.item.id}
            onSummaryOpenChange={(open) =>
              onOpenItemChange(open ? placed.item.id : null)
            }
            dayWidth={dayWidth}
            onSelect={onSelectItem}
            onDragStart={onDragStart}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Timeline: days across; a collapsible Projects section (one row per
 * project), then one row per type for items without a project.
 * Single-day items are a milestone diamond on their day (title inside the
 * day on wide zooms); multi-day tasks are bars from start_date to due date.
 * ⌘/Ctrl + wheel zooms around the cursor. Drag to move, drag a bar's edge to change its start
 * or end (undoable, same as the calendar).
 */
export const WorkTimelineView = ({
  items,
  range,
  restScrollDays = 0,
  onReachEdge,
  dayWidth,
  onDayWidthChange,
  onSelectItem,
  onRescheduleEvent,
  onRescheduleTaskRange,
  projects = [],
  emptyMessage,
}: {
  items: WorkItem[];
  /** ⌘/Ctrl + wheel zoom (px per day); the day under the cursor stays put. */
  onDayWidthChange: (dayWidth: number) => void;
  /** Days scrolled past on a fresh range (the off-screen buffer before it). */
  restScrollDays?: number;
  /** Scrolled into the start (-1) or end (1) buffer: shift the range. */
  onReachEdge?: (direction: -1 | 1) => void;
  /** Projects whose start → delivery span is drawn as a summary bar. */
  projects?: TimelineProjectSpan[];
  range: TimelineRange;
  dayWidth: number;
  onSelectItem: (item: WorkItem) => void;
  onRescheduleEvent: (
    event: CalendarEvent,
    targetDateKey: string,
  ) => void | Promise<void>;
  onRescheduleTaskRange: (
    task: Task,
    startDateKey: string,
    dueDateKey: string,
  ) => void;
  emptyMessage: string;
}) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set(),
  );
  const todayKey = toDateKey(new Date());
  const draggingRef = useRef<TimelineDrag | null>(null);
  const [labelWidth, setLabelWidth] = useTimelineLabelWidth();
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const zoomStateRef = useRef({ dayWidth, onDayWidthChange });
  zoomStateRef.current = { dayWidth, onDayWidthChange };
  const pendingZoomAnchorRef = useRef<{ day: number; offset: number } | null>(
    null,
  );

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const { dayWidth: width, onDayWidthChange: change } =
        zoomStateRef.current;
      const offset =
        event.clientX - element.getBoundingClientRect().left - labelWidth;
      if (offset < 0) return;
      const next = clampTimelineDayWidth(
        width * Math.exp(-event.deltaY * 0.004),
      );
      if (next === width) return;
      pendingZoomAnchorRef.current = {
        day: (element.scrollLeft + offset) / width,
        offset,
      };
      change(next);
    };
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, []);

  // Endless scroll: near either end, ask for a shifted range; when it
  // arrives, offset scrollLeft by the shift so the view does not jump.
  // Any other range change (Today, arrows, first render) rests on the
  // anchor week.
  const previousStartRef = useRef<number | null>(null);
  const edgeShiftPendingRef = useRef(false);
  const rangeStartTime = range.start.getTime();

  useLayoutEffect(() => {
    const element = scrollRef.current;
    const previous = previousStartRef.current;
    previousStartRef.current = rangeStartTime;
    if (!element) return;
    if (edgeShiftPendingRef.current && previous != null) {
      edgeShiftPendingRef.current = false;
      const shiftedDays = Math.round(
        (rangeStartTime - previous) / (24 * 60 * 60 * 1000),
      );
      element.scrollLeft -= shiftedDays * dayWidth;
      return;
    }
    element.scrollLeft = restScrollDays * dayWidth;
    // Only a new range start re-positions; zoom keeps its own anchor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartTime]);

  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element || !onReachEdge || edgeShiftPendingRef.current) return;
    const edge = Math.max(dayWidth * 7, 200);
    if (element.scrollLeft < edge) {
      edgeShiftPendingRef.current = true;
      onReachEdge(-1);
    } else if (
      element.scrollLeft + element.clientWidth >
      element.scrollWidth - edge
    ) {
      edgeShiftPendingRef.current = true;
      onReachEdge(1);
    }
  };

  // Keep the day that was under the cursor in place after a wheel zoom.
  useLayoutEffect(() => {
    const anchor = pendingZoomAnchorRef.current;
    const element = scrollRef.current;
    if (!anchor || !element) return;
    pendingZoomAnchorRef.current = null;
    element.scrollLeft = Math.max(0, anchor.day * dayWidth - anchor.offset);
  }, [dayWidth]);

  const groups = useMemo(
    () => buildTimelineGroups(items, range, dayWidth, projects),
    [dayWidth, items, projects, range],
  );
  const projectGroups = groups.filter((group) => group.kind === "project");
  const otherGroups = groups.filter((group) => group.kind === "category");
  const monthSegments = useMemo(
    () => getTimelineMonthSegments(range.days),
    [range.days],
  );

  const today = new Date();
  const todayIndex = range.days.findIndex((day) => isSameDay(day, today));
  const canvasWidth = range.days.length * dayWidth;

  const toggleGroup = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleDrop = (dropIndex: number) => {
    const drag = draggingRef.current;
    draggingRef.current = null;
    if (!drag) return;
    const { item, mode, grabOffset } = drag;
    const dropKey = toDateKey(addDays(range.start, dropIndex));

    if (isTaskRange(item) && item.event.kind === "task") {
      const task = item.event.task;
      const startKey = item.startDate!;
      if (mode === "start") {
        onRescheduleTaskRange(
          task,
          dropKey > item.date ? item.date : dropKey,
          item.date,
        );
        return;
      }
      if (mode === "end") {
        onRescheduleTaskRange(
          task,
          startKey,
          dropKey < startKey ? startKey : dropKey,
        );
        return;
      }
      const delta =
        dropIndex - grabOffset - getTimelineDayIndex(range.start, startKey);
      if (delta === 0) return;
      onRescheduleTaskRange(
        task,
        toDateKey(addDays(parseDateKey(startKey), delta)),
        toDateKey(addDays(parseDateKey(item.date), delta)),
      );
      return;
    }

    if (dropKey === item.date) return;
    void onRescheduleEvent(item.event, dropKey);
  };

  return (
    <div
      className="overflow-hidden rounded-xl border bg-card"
      style={{ "--timeline-label-width": `${labelWidth}px` } as CSSProperties}
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="max-h-[calc(100vh-16rem)] overflow-auto"
      >
        <div className="relative" style={{ width: labelWidth + canvasWidth }}>
          {/* Header: months + day numbers */}
          <div className="sticky top-0 z-30 flex border-b bg-card">
            <div
              className="sticky left-0 z-40 shrink-0 border-r bg-card"
              style={{ width: LABEL_WIDTH_VAR }}
            >
              <LabelColumnResizer width={labelWidth} onResize={setLabelWidth} />
            </div>
            <div className="shrink-0" style={{ width: canvasWidth }}>
              <div className="relative h-7 border-b">
                {monthSegments.map((segment) => (
                  <div
                    key={segment.key}
                    className="absolute inset-y-0 flex items-center border-l px-2 text-xs font-semibold text-foreground first:border-l-0"
                    style={{
                      left: segment.startIndex * dayWidth,
                      width: segment.length * dayWidth,
                    }}
                  >
                    <span className="truncate">{segment.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex h-10">
                {range.days.map((day, index) => {
                  const isToday = index === todayIndex;
                  return (
                    <div
                      key={toDateKey(day)}
                      className={cn(
                        "flex shrink-0 flex-col items-center justify-center gap-0.5 border-r border-border leading-none",
                        isWeekend(day) && "bg-muted/50",
                        day.getDay() === 0 && "border-l border-l-border/80",
                      )}
                      style={{ width: dayWidth }}
                    >
                      {dayWidth >= 22 ? (
                        <span className="text-2xs text-muted-foreground uppercase">
                          {day.toLocaleDateString("en-US", {
                            weekday: dayWidth >= 80 ? "short" : "narrow",
                          })}
                        </span>
                      ) : null}
                      {dayWidth >= 22 || isToday || day.getDay() === 0 ? (
                        <span
                          className={cn(
                            "inline-flex items-center justify-center rounded-full tabular-nums",
                            dayWidth >= 22
                              ? "size-5 text-xs"
                              : "size-4 text-3xs",
                            isToday
                              ? "bg-primary font-semibold text-primary-foreground"
                              : "text-foreground",
                          )}
                        >
                          {day.getDate()}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Day grid behind the rows */}
          <div
            className="pointer-events-none absolute bottom-0 flex"
            style={{ left: labelWidth, top: 68, width: canvasWidth }}
            aria-hidden
          >
            {range.days.map((day) => (
              <div
                key={toDateKey(day)}
                className={cn(
                  "h-full shrink-0 border-r border-border",
                  isWeekend(day) && "bg-muted/50",
                  day.getDay() === 0 && "border-l border-l-border/80",
                )}
                style={{ width: dayWidth }}
              />
            ))}
            {todayIndex >= 0 ? (
              <div
                className="absolute inset-y-0 w-0.5 bg-primary"
                style={{ left: todayIndex * dayWidth + dayWidth / 2 - 1 }}
              >
                <span className="absolute -top-1 -left-[3px] size-2 rounded-full bg-primary" />
              </div>
            ) : null}
          </div>

          {groups.length === 0 ? (
            <div className="relative flex min-h-48">
              <div
                className="sticky left-0 shrink-0 border-r bg-card"
                style={{ width: LABEL_WIDTH_VAR }}
              />
              <p className="self-center px-6 text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            </div>
          ) : (
            <div className="relative">
              {projectGroups.length > 0 ? (
                <TimelineSectionRow
                  label="Projects"
                  count={projectGroups.length}
                  canvasWidth={canvasWidth}
                  collapsed={collapsed.has(PROJECTS_SECTION_KEY)}
                  onToggle={() => toggleGroup(PROJECTS_SECTION_KEY)}
                />
              ) : null}
              {[
                ...(collapsed.has(PROJECTS_SECTION_KEY) ? [] : projectGroups),
                ...otherGroups,
              ].map((group) => (
                <TimelineGroupRow
                  key={group.key}
                  group={group}
                  range={range}
                  dayWidth={dayWidth}
                  indent={group.kind === "project"}
                  expanded={expandedRows.has(group.key)}
                  onToggle={() =>
                    setExpandedRows((current) => {
                      const next = new Set(current);
                      if (next.has(group.key)) next.delete(group.key);
                      else next.add(group.key);
                      return next;
                    })
                  }
                  todayKey={todayKey}
                  onSelectItem={onSelectItem}
                  openItemId={openItemId}
                  onOpenItemChange={setOpenItemId}
                  onDragStart={(drag) => {
                    draggingRef.current = drag;
                  }}
                  onDropOnDay={handleDrop}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
